import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://13.60.38.155:5001';

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
  // withCredentials: true, // Important for CORS with credentials
});

// Add request interceptor for handling errors
api.interceptors.request.use(
  (config) => {
    // First try to get from localStorage
    let walletAddress = localStorage.getItem('walletAddress');

    // Set the header only if we have a wallet address
    if (walletAddress) {
      config.headers['X-Wallet-Address'] = walletAddress;
      console.log('Sending request with wallet address header:', walletAddress);

      // Also add to body for POST/PUT requests as a fallback
      if (
        (config.method === 'post' || config.method === 'put') &&
        config.data
      ) {
        try {
          let data = config.data;
          if (typeof data === 'string') {
            data = JSON.parse(data);
          }
          // Only add walletAddress if it doesn't already exist
          if (!data.walletAddress) {
            data.walletAddress = walletAddress;
            config.data = JSON.stringify(data);
          }
        } catch (e) {
          console.error('Error adding walletAddress to request body:', e);
        }
      }
    } else {
      console.log('No wallet address found in localStorage');
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor for handling errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response) {
      // Server responded with error status
      console.error('Response Error:', error.response.data);
      return Promise.reject(error.response.data);
    } else if (error.request) {
      // Request was made but no response
      console.error('Request Error:', error.request);
      return Promise.reject({ message: 'No response from server' });
    } else {
      // Something else happened
      console.error('Error:', error.message);
      return Promise.reject({ message: error.message });
    }
  }
);

// API endpoints
export const projectAPI = {
  create: (data) => api.post('/projects', data),
  getAll: () => api.get('/projects'),
  getAllPublic: () => api.get('/projects/public'),
  getByIdPublic: (projectTokenId) =>
    api.get(`/projects/public/token/${projectTokenId}`),
  getById: (projectTokenId) => api.get(`/projects/token/${projectTokenId}`),
  update: (projectTokenId, data) =>
    api.put(`/projects/token/${projectTokenId}`, data),
  delete: (projectTokenId) => api.delete(`/projects/token/${projectTokenId}`),
  requestCollaboration: (projectTokenId, data) =>
    api.post(`/projects/token/${projectTokenId}/collaborate`, data),
  getByCreator: (walletAddress) =>
    api.get(`/projects/creator/${walletAddress}`),
  getByContributor: (walletAddress) =>
    api.get(`/projects/contributor/${walletAddress}`),
  getByInvestor: (walletAddress) =>
    api.get(`/projects/investor/${walletAddress}`),
  getByStaker: (walletAddress) => api.get(`/projects/staker/${walletAddress}`),
  investInProject: (projectTokenId, data) =>
    api.patch(`/projects/${projectTokenId}/invest`, data),
  stakeInProject: (projectTokenId, data) =>
    api.patch(`/projects/${projectTokenId}/stake`, data),
};

export const missionAPI = {
  create: (data) => {
    console.log('Sending mission data to API:', data); // Debug log
    return api.post('/missions', data);
  },
  getAll: () => api.get('/missions'),
  getById: (id) => api.get(`/missions/${id}`),
  update: (id, data) => api.put(`/missions/${id}`, data),
  delete: (id) => api.delete(`/missions/${id}`),
  apply: (id) => api.post(`/missions/${id}/apply`),
};

// Innovator API endpoints for interacting with innovator data
export const innovatorAPI = {
  create: (data) => {
    console.log('Sending innovator data to API:', data); // Debug log
    return api.post('/innovators', data);
  },
  getAll: () => {
    console.log('Fetching all innovators...'); // Debug log
    return api.get('/innovators');
  },
  getByWalletAddress: (address) => api.get(`/innovators/wallet/${address}`),
  update: (walletAddress, data) =>
    api.put(`/innovators/wallet/${walletAddress}`, data),
  delete: (walletAddress) => api.delete(`/innovators/wallet/${walletAddress}`),
};

export const fileAPI = {
  upload: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/files/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  delete: (key) => api.delete(`/files/${key}`),
};

export const analyticsAPI = {
  trackEvent: (data) => api.post('/analytics/track', data),
  getItemAnalytics: (id) => api.get(`/analytics/item/${id}`),
};

export const collaborationAPI = {
  create: (data) => {
    console.log('Sending collaboration request:', data); // Debug log
    return api.post('/collaborations', data);
  },
  getAll: () => {
    console.log('Fetching all collaboration requests...'); // Debug log
    return api.get('/collaborations');
  },
  getById: (id) => api.get(`/collaborations/${id}`),
  update: (id, data) => {
    console.log('Updating collaboration:', id, data);
    return api.put(`/collaborations/${id}`, data);
  },
  delete: (id) => api.delete(`/collaborations/${id}`),
  getByProject: (projectId) => {
    console.log('Fetching collaborations for project:', projectId);
    return api.get(`/collaborations/project/${projectId}`);
  },
  getByUser: (walletAddress) =>
    api.get(`/collaborations/user/${walletAddress}`),
};

export const taskAPI = {
  create: (data) => {
    console.log('Creating new task:', data); // Debug log
    return api.post('/tasks', data);
  },
  getByProject: (projectId) => {
    console.log('Fetching tasks for project:', projectId);
    return api.get(`/tasks/project/${projectId}`);
  },
  getById: (id) => api.get(`/tasks/${id}`),
  update: (id, data) => api.put(`/tasks/${id}`, data),
  delete: (id) => api.delete(`/tasks/${id}`),
  applyForTask: (id, data) => {
    console.log('Applying for task:', id, data);
    return api.post(`/tasks/${id}/apply`, data);
  },
  respondToApplication: (applicationId, data) => {
    console.log('Responding to application:', applicationId, data);
    return api.patch(`/tasks/application/${applicationId}`, data);
  },
  claimTask: (id, data) => {
    console.log('Claiming task:', id, data);
    return api.post(`/tasks/${id}/claim`, data);
  },
  submitWork: (id, data) => {
    console.log('Submitting work for task:', id, data);
    return api.post(`/tasks/${id}/submit`, data);
  },
  reviewSubmission: (submissionId, data) => {
    console.log('Reviewing submission:', submissionId, data);
    return api.patch(`/tasks/submission/${submissionId}`, data);
  },
  releaseIUs: (taskId, data) => {
    console.log('Releasing IUs for task:', taskId, data);
    return api.post(`/tasks/${taskId}/release-ius`, data);
  },
  getByInnovator: (walletAddress) => {
    console.log('Fetching tasks for innovator:', walletAddress);
    return api.get(`/innovators/wallet/${walletAddress}/tasks`);
  },
  getByWalletAddress: (walletAddress) => {
    console.log('Fetching tasks by WalletAddress:', walletAddress);
    return api.get(`/tasks/wallet/${walletAddress}/tasks`);
  },

  getByWalletAddress: (walletAddress) =>
    api.get(`/tasks/wallet/${walletAddress}`),
  getUserTasks: (walletAddress) => api.get(`/tasks/user/${walletAddress}`),
  getProjectById: (projectId) => api.get(`/tasks/project/${projectId}`),
};

export default api;
